/**
 * Share an inspection's report (docs/inspections-platform.md, section 11):
 * email someone the inspection names a read-only link to the report, which
 * works for the tenant's number of days. Mounted only while open, so every
 * share starts from a clean form.
 */

import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { toastApiError } from '@/shared/lib/api-error'
import { useForm, type FormField } from '@/shared/lib/forms'
import { useReportShare, useShareReport } from '@/admin/hooks/use-inspections'

export function ShareReportDialog({
  inspectionUuid,
  onClose,
}: {
  inspectionUuid: string
  onClose: () => void
}) {
  const { t } = useTranslation()
  const { data, isPending } = useReportShare(inspectionUuid, true)
  const share = useShareReport(inspectionUuid)
  // Only someone with an email address can be sent a link.
  const reachable = useMemo(
    () => (data?.parties ?? []).filter((party) => party.email),
    [data],
  )

  const fields = useMemo<FormField[]>(
    () => [
      {
        type: 'select',
        name: 'party_key',
        label: t('inspections.report.shareWith', 'Send to'),
        required: t('inspections.report.shareWithRequired', 'Pick who to send it to'),
        value: reachable.length === 1 ? reachable[0].key : '',
        options: reachable.map((party) => ({
          value: party.key,
          label: `${party.label ? `${party.label}: ` : ''}${party.name} (${party.email})`,
        })),
        hint:
          !isPending && reachable.length === 0
            ? t(
                'inspections.report.noOneToShareWith',
                'No one on this inspection has an email address yet.',
              )
            : undefined,
      },
    ],
    [reachable, isPending, t],
  )

  const { Form, ref } = useForm(
    { name: 'share-report', submit: () => <></>, fields },
    async (values) => {
      try {
        const sent = await share.mutateAsync(String(values.party_key))
        toast.success(
          t('inspections.report.shared', 'Report link sent to {{name}}.', { name: sent.name }),
        )
        onClose()
      } catch (err) {
        toastApiError(err, t('inspections.report.shareFailed', "Couldn't send the report link."))
      }
    },
    [share, onClose, t],
  )

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[480px]" data-testid="share-report-dialog">
        <DialogHeader>
          <DialogTitle>{t('inspections.report.shareTitle', 'Share the report')}</DialogTitle>
          <DialogDescription>
            {t(
              'inspections.report.shareHint',
              'They get an email with a private link to read the report. The link works for {{days}} days.',
              { days: data?.link_days ?? 30 },
            )}
          </DialogDescription>
        </DialogHeader>
        {/* Who it can go to loads first, so the picker never opens empty. */}
        {isPending ? (
          <div className="flex items-center gap-2 py-2 text-[12px] text-[var(--ink-3)]">
            <Loader2 size={14} className="animate-spin" />
            {t('common.loading', 'Loading...')}
          </div>
        ) : (
          <Form className="flex flex-col gap-3" />
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={() => ref.current?.submitForm()}
            disabled={share.isPending}
            data-testid="share-report-submit"
          >
            {share.isPending
              ? t('inspections.report.sharing', 'Sending...')
              : t('inspections.report.shareSubmit', 'Send link')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
